import { type ReactElement, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  presaleApi,
  vaultApi,
  type Presale,
  type PresaleContribution,
  type VaultStatus,
  type RewardEpoch,
} from '../../lib/presale-api';

// ── Countdown Hook ─────────────────────────────────────

function useCountdown(endsAt: string): string {
  const [timeLeft, setTimeLeft] = useState('');
  useEffect(() => {
    const tick = () => {
      const diff = new Date(endsAt).getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft('Ended');
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(
        `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
      );
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endsAt]);
  return timeLeft;
}

// ── Inline SVG Icons ────────────────────────────────────

function IconX(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.259 5.63 5.906-5.63zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function IconGlobe(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

function IconDocument(): ReactElement {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

function IconUser(): ReactElement {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function IconPie(): ReactElement {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
      <path d="M22 12A10 10 0 0 0 12 2v10z" />
    </svg>
  );
}

function IconFunds(): ReactElement {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}

function IconVault(): ReactElement {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function IconLightning(): ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  );
}

function IconCheck(): ReactElement {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function IconArrowLeft(): ReactElement {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

function IconSettings(): ReactElement {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function IconDollar(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#c4f70e"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}

// ── Section Label ────────────────────────────────────────

function SectionLabel({ icon, text }: { icon: ReactElement; text: string }): ReactElement {
  return (
    <div className="mb-5 flex items-center gap-2">
      <span className="text-white/30">{icon}</span>
      <span
        className="text-[11px] font-semibold uppercase text-white/40"
        style={{ letterSpacing: '2px' }}
      >
        {text}
      </span>
    </div>
  );
}

// ── Donut Chart ──────────────────────────────────────────

const SEGMENT_COLORS = ['#c4f70e', '#8bc34a', '#4caf50', '#66bb6a', '#a5d6a7'];

function DonutChart({ contributions }: { contributions: PresaleContribution[] }): ReactElement {
  const total = contributions.reduce((s, c) => s + Number(c.percentage), 0) || 1;
  let cumulative = 0;

  const segments = contributions.slice(0, 5).map((c, i) => {
    const pct = (Number(c.percentage) / total) * 100;
    const startAngle = (cumulative / 100) * 360 - 90;
    cumulative += pct;
    const endAngle = (cumulative / 100) * 360 - 90;
    const r = 50;
    const cx = 70;
    const cy = 70;
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    const x1 = cx + r * Math.cos(startRad);
    const y1 = cy + r * Math.sin(startRad);
    const x2 = cx + r * Math.cos(endRad);
    const y2 = cy + r * Math.sin(endRad);
    const large = pct > 50 ? 1 : 0;
    const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
    return { d, color: SEGMENT_COLORS[i % SEGMENT_COLORS.length] };
  });

  return (
    <svg width="140" height="140" viewBox="0 0 140 140">
      {contributions.length > 0 ? (
        <>
          {segments.map((seg, i) => (
            <path key={i} d={seg.d} fill={seg.color} opacity="0.9" />
          ))}
          <circle cx="70" cy="70" r="30" fill="#08080a" />
        </>
      ) : (
        <>
          <circle cx="70" cy="70" r="50" fill="rgba(255,255,255,0.03)" />
          <circle cx="70" cy="70" r="30" fill="#08080a" />
        </>
      )}
      <text
        x="70"
        y="66"
        textAnchor="middle"
        fill="rgba(255,255,255,0.4)"
        fontSize="9"
        fontFamily="monospace"
      >
        SPOTS
      </text>
      <text
        x="70"
        y="79"
        textAnchor="middle"
        fill="white"
        fontSize="13"
        fontWeight="bold"
        fontFamily="monospace"
      >
        {contributions.length}
      </text>
    </svg>
  );
}

// ── Pre-Launch View ─────────────────────────────────────

function PreLaunchView({
  presale,
  onRefresh,
}: {
  presale: Presale;
  onRefresh: () => void;
}): ReactElement {
  const countdown = useCountdown(presale.endsAt);
  const progress = Math.min((Number(presale.totalRaised) / Number(presale.targetSol)) * 100, 100);
  const [amount, setAmount] = useState('');
  const [contributing, setContributing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const spotsLeft = presale.maxBuyers - presale.buyerCount;
  const contributions = presale.contributions ?? [];

  const handleContribute = async (): Promise<void> => {
    const sol = parseFloat(amount);
    if (!sol || sol <= 0) {
      setError('Enter a valid SOL amount');
      return;
    }
    setContributing(true);
    setError(null);
    try {
      // Extract wallet address from Privy localStorage
      let pubkey: string | null = null;
      try {
        const raw = localStorage.getItem('privy:connections');
        if (raw) {
          const connections = JSON.parse(raw) as Array<{ type: string; address?: string }>;
          const wallet = connections.find((c) => c.type === 'wallet');
          if (wallet?.address) pubkey = wallet.address;
        }
      } catch {
        /* fallback */
      }
      if (!pubkey) {
        setError('Wallet not connected. Please connect your wallet to contribute.');
        setContributing(false);
        return;
      }
      await presaleApi.contribute(presale.id, sol, pubkey);
      setAmount('');
      onRefresh();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Contribution failed');
    } finally {
      setContributing(false);
    }
  };

  const cardStyle =
    'bg-white/[0.015] border border-white/[0.06] rounded-[14px] p-9 transition-all duration-300 hover:border-[#c4f70e]/[0.15] hover:shadow-[0_0_40px_rgba(196,247,14,0.08)]';

  return (
    <div className="flex gap-8" style={{ alignItems: 'flex-start' }}>
      {/* ── LEFT COLUMN ── */}
      <div className="min-w-0 space-y-6" style={{ flex: 2 }}>
        {/* 1. Token Header */}
        <div
          className="border border-white/[0.06] bg-white/[0.015] p-9"
          style={{
            clipPath:
              'polygon(0 0, calc(100% - 24px) 0, 100% 24px, 100% 100%, 24px 100%, 0 calc(100% - 24px))',
          }}
        >
          <div className="flex items-start gap-6">
            {/* Avatar with gradient ring */}
            <div className="relative flex-shrink-0" style={{ width: 88, height: 88 }}>
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background: 'conic-gradient(#c4f70e, #8bc34a, #4caf50, #c4f70e)',
                  padding: 2,
                  boxShadow: '0 0 24px rgba(196,247,14,0.3)',
                  borderRadius: '50%',
                }}
              />
              <div
                className="absolute overflow-hidden rounded-full"
                style={{ inset: 2, background: '#08080a' }}
              >
                {presale.imageUrl ? (
                  <img
                    src={presale.imageUrl}
                    alt={presale.tokenName}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-[28px] font-bold text-[#c4f70e]">
                    {presale.tokenSymbol.charAt(0)}
                  </div>
                )}
              </div>
            </div>

            {/* Title block */}
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="text-[32px] font-bold leading-tight text-white">
                    {presale.tokenName}
                  </h1>
                  <p className="mt-1 font-mono text-white/40" style={{ fontSize: 14 }}>
                    ${presale.tokenSymbol}
                  </p>
                </div>
                <span
                  className="flex-shrink-0 rounded-full px-3 py-1 text-[11px] font-semibold uppercase"
                  style={{
                    background: 'rgba(196,247,14,0.1)',
                    color: '#c4f70e',
                    letterSpacing: '1px',
                    border: '1px solid rgba(196,247,14,0.2)',
                  }}
                >
                  {presale.status}
                </span>
              </div>

              {/* Social links */}
              <div className="mt-4 flex items-center gap-3">
                {presale.twitter && (
                  <a
                    href={presale.twitter}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1.5 rounded-lg border border-white/[0.06] px-3 py-1.5 text-white/30 transition-colors hover:border-[#c4f70e]/30 hover:text-[#c4f70e]"
                    style={{ fontSize: 12 }}
                  >
                    <IconX />
                    Twitter
                  </a>
                )}
                {presale.website && (
                  <a
                    href={presale.website}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1.5 rounded-lg border border-white/[0.06] px-3 py-1.5 text-white/30 transition-colors hover:border-[#c4f70e]/30 hover:text-[#c4f70e]"
                    style={{ fontSize: 12 }}
                  >
                    <IconGlobe />
                    Website
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 2. The Pitch */}
        <div className={cardStyle}>
          <SectionLabel icon={<IconDocument />} text="The Pitch" />
          <p className="text-white/70" style={{ fontSize: 16, lineHeight: 2.0 }}>
            {(presale.pitch ?? presale.description ?? '')
              .split(/\b(SOL|token|launch|vault|reward|buyer|presale)\b/gi)
              .map((part, i) =>
                /^(SOL|token|launch|vault|reward|buyer|presale)$/i.test(part) ? (
                  <span key={i} style={{ color: '#c4f70e' }}>
                    {part}
                  </span>
                ) : (
                  <span key={i}>{part}</span>
                )
              )}
          </p>
        </div>

        {/* 3. Launcher Profile */}
        <div className={cardStyle}>
          <SectionLabel icon={<IconUser />} text="Launcher Profile" />
          <div className="flex items-start gap-5">
            {/* Avatar */}
            <div
              className="flex-shrink-0 overflow-hidden rounded-full"
              style={{
                width: 64,
                height: 64,
                background: 'rgba(196,247,14,0.08)',
                border: '1px solid rgba(196,247,14,0.15)',
              }}
            >
              {presale.launcher?.avatarUrl ? (
                <img
                  src={presale.launcher.avatarUrl}
                  alt={presale.launcher.displayName ?? 'Launcher'}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-[22px] font-bold text-[#c4f70e]">
                  {(presale.launcher?.displayName ?? 'A').charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="min-w-0 flex-1">
              <p className="text-[20px] font-bold leading-tight text-white">
                {presale.launcher?.displayName ?? 'Anonymous'}
              </p>
              {presale.launcher?.twitterHandle && (
                <p className="mt-0.5 text-white/40" style={{ fontSize: 13 }}>
                  @{presale.launcher.twitterHandle}
                </p>
              )}

              {/* Stat boxes */}
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div
                  className="rounded-xl p-5 text-center"
                  style={{
                    background: 'rgba(255,255,255,0.025)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <p className="font-mono font-bold" style={{ fontSize: 24, color: '#c4f70e' }}>
                    {presale.launcher?.presaleCount ?? 0}
                  </p>
                  <p className="mt-1 text-white/40" style={{ fontSize: 11 }}>
                    Launches
                  </p>
                </div>
                <div
                  className="rounded-xl p-5 text-center"
                  style={{
                    background: 'rgba(255,255,255,0.025)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <p className="font-mono font-bold text-white" style={{ fontSize: 24 }}>
                    {Number(presale.totalRaised).toFixed(1)}
                  </p>
                  <p className="mt-1 text-white/40" style={{ fontSize: 11 }}>
                    SOL Raised
                  </p>
                </div>
                <div
                  className="rounded-xl p-5 text-center"
                  style={{
                    background: 'rgba(255,255,255,0.025)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <p className="font-mono font-bold text-white" style={{ fontSize: 24 }}>
                    {presale.buyerCount}
                  </p>
                  <p className="mt-1 text-white/40" style={{ fontSize: 11 }}>
                    Investors
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 4. Token Allocation */}
        <div className={cardStyle}>
          <SectionLabel icon={<IconPie />} text="Token Allocation" />
          {contributions.length > 0 ? (
            <div className="flex items-start gap-8">
              <div className="flex-shrink-0">
                <DonutChart contributions={contributions} />
              </div>
              <div className="min-w-0 flex-1 space-y-3 pt-2">
                {contributions.slice(0, 5).map((c, i) => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between gap-4 border-b border-white/[0.04] py-2 last:border-0"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <div
                        className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                        style={{ background: SEGMENT_COLORS[i % SEGMENT_COLORS.length] }}
                      />
                      <span className="truncate font-mono text-white/50" style={{ fontSize: 12 }}>
                        {c.buyerPublicKey.slice(0, 4)}…{c.buyerPublicKey.slice(-4)}
                      </span>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-4">
                      <span className="font-mono text-white" style={{ fontSize: 13 }}>
                        {Number(c.amountSol).toFixed(2)} SOL
                      </span>
                      <span className="font-mono text-white/40" style={{ fontSize: 12 }}>
                        {Number(c.percentage).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                ))}
                {contributions.length === 0 && (
                  <p className="text-white/30" style={{ fontSize: 13 }}>
                    No contributors yet. Be the first!
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-6">
              <DonutChart contributions={[]} />
              <p className="text-white/30" style={{ fontSize: 14 }}>
                No contributions yet. Be the first investor.
              </p>
            </div>
          )}
        </div>

        {/* 5. Use of Funds */}
        {presale.useOfFunds && presale.useOfFunds.length > 0 && (
          <div className={cardStyle}>
            <SectionLabel icon={<IconFunds />} text="Use of Funds" />
            <div className="space-y-4">
              {presale.useOfFunds.map((item, i) => (
                <div key={item.label}>
                  <div className="mb-1.5 flex justify-between">
                    <span className="text-white/70" style={{ fontSize: 13 }}>
                      {item.label}
                    </span>
                    <span className="font-mono text-white/50" style={{ fontSize: 12 }}>
                      {item.percentage}%
                    </span>
                  </div>
                  <div
                    className="overflow-hidden rounded-full"
                    style={{ height: 14, background: 'rgba(255,255,255,0.04)' }}
                  >
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${item.percentage}%`,
                        background:
                          i === 0
                            ? 'linear-gradient(90deg, #c4f70e, #8bc34a)'
                            : i === 1
                              ? '#8bc34a'
                              : '#4caf50',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 6. Reward Vault Preview */}
        <div
          className="rounded-[14px] p-9 transition-all duration-300"
          style={{ background: 'rgba(196,247,14,0.02)', border: '1px solid rgba(196,247,14,0.12)' }}
        >
          <div className="mb-6 flex items-start gap-4">
            <div
              className="flex flex-shrink-0 items-center justify-center rounded-lg"
              style={{
                width: 40,
                height: 40,
                background: 'rgba(196,247,14,0.1)',
                border: '1px solid rgba(196,247,14,0.2)',
              }}
            >
              <IconDollar />
            </div>
            <div>
              <SectionLabel icon={<IconVault />} text="Reward Vault" />
              <p className="-mt-3 text-white/60" style={{ fontSize: 14, lineHeight: 1.7 }}>
                Every trade fee from your token flows into the Reward Vault. Each epoch, 95% is
                distributed pro-rata to presale buyers.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Epoch Cadence', value: presale.epochCadence || 'DAILY' },
              { label: 'Platform Fee', value: '5%' },
              { label: 'Buyer Share', value: '95%' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl p-5 text-center"
                style={{
                  background: 'rgba(255,255,255,0.025)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <p className="font-mono font-bold text-[#c4f70e]" style={{ fontSize: 18 }}>
                  {stat.value}
                </p>
                <p className="mt-1 text-white/40" style={{ fontSize: 11 }}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── RIGHT SIDEBAR ── */}
      <div style={{ flex: 1, minWidth: 280 }}>
        <div
          className="sticky border border-white/[0.06] bg-white/[0.015]"
          style={{
            top: 24,
            clipPath:
              'polygon(0 0, calc(100% - 24px) 0, 100% 24px, 100% 100%, 24px 100%, 0 calc(100% - 24px))',
          }}
        >
          <div className="space-y-6 p-7">
            {/* Progress */}
            <div>
              <div className="mb-2 flex justify-between" style={{ fontSize: 12 }}>
                <span className="font-mono text-white/60">
                  {Number(presale.totalRaised).toFixed(2)} / {Number(presale.targetSol).toFixed(2)}{' '}
                  SOL
                </span>
                <span className="font-mono text-[#c4f70e]">{progress.toFixed(0)}%</span>
              </div>
              <div
                className="relative h-2.5 overflow-hidden rounded-full"
                style={{ background: 'rgba(255,255,255,0.06)' }}
              >
                <div
                  className="relative h-full overflow-hidden rounded-full transition-all duration-700"
                  style={{
                    width: `${progress}%`,
                    background: 'linear-gradient(90deg, #c4f70e, #8bc34a)',
                  }}
                >
                  {/* Shine */}
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
                      animation: 'shimmer 2s infinite',
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Countdown */}
            <div className="text-center">
              <p
                className="mb-1 text-white/30"
                style={{ fontSize: 11, letterSpacing: '2px', textTransform: 'uppercase' }}
              >
                Time Remaining
              </p>
              <span
                className="font-mono font-bold text-white"
                style={{
                  fontSize: 40,
                  textShadow: '0 0 20px rgba(196,247,14,0.4)',
                  display: 'block',
                }}
              >
                {countdown}
              </span>
            </div>

            {/* Spot indicators */}
            <div>
              <p
                className="mb-3 text-white/30"
                style={{ fontSize: 11, letterSpacing: '2px', textTransform: 'uppercase' }}
              >
                Spots — {spotsLeft} remaining
              </p>
              <div className="flex flex-wrap gap-2">
                {Array.from({ length: Math.min(presale.maxBuyers, 20) }).map((_, i) => {
                  const filled = i < presale.buyerCount;
                  return (
                    <div
                      key={i}
                      className="flex items-center justify-center rounded-full transition-all duration-300"
                      style={{
                        width: 28,
                        height: 28,
                        border: filled
                          ? '1.5px solid #c4f70e'
                          : '1.5px dashed rgba(255,255,255,0.15)',
                        background: filled ? 'rgba(196,247,14,0.08)' : 'transparent',
                        boxShadow: filled ? '0 0 10px rgba(196,247,14,0.2)' : 'none',
                        color: filled ? '#c4f70e' : 'rgba(255,255,255,0.15)',
                      }}
                    >
                      {filled && <IconCheck />}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* SOL Input */}
            <div>
              <label
                className="mb-1.5 block text-white/40"
                style={{ fontSize: 11, letterSpacing: '1px', textTransform: 'uppercase' }}
              >
                Amount (SOL)
              </label>
              <input
                type="number"
                step="0.01"
                min={Number(presale.minContribution)}
                max={Number(presale.maxContribution)}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={`${Number(presale.minContribution)} – ${Number(presale.maxContribution)}`}
                className="w-full rounded-xl border bg-white/[0.04] px-4 py-3 font-mono text-white placeholder:text-white/20 focus:outline-none"
                style={{
                  borderColor: 'rgba(255,255,255,0.1)',
                  fontSize: 15,
                  transition: 'border-color 0.2s',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(196,247,14,0.5)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                }}
              />
              <div className="mt-2 flex justify-between text-white/30" style={{ fontSize: 11 }}>
                <span>Min: {Number(presale.minContribution)} SOL</span>
                <span>Max: {Number(presale.maxContribution)} SOL</span>
              </div>
            </div>

            {error && (
              <p className="text-red-400" style={{ fontSize: 12 }}>
                {error}
              </p>
            )}

            {/* CTA Button */}
            <button
              onClick={handleContribute}
              disabled={contributing || presale.status !== 'OPEN'}
              className="relative w-full overflow-hidden rounded-xl font-semibold text-black transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50"
              style={{
                background: '#c4f70e',
                padding: '14px 24px',
                fontSize: 15,
                boxShadow: '0 4px 24px rgba(196,247,14,0.25)',
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.boxShadow = '0 8px 40px rgba(196,247,14,0.5)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 24px rgba(196,247,14,0.25)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {contributing ? 'Contributing…' : 'Contribute SOL'}
            </button>

            {/* Launcher mini-profile */}
            <div className="border-t border-white/[0.06] pt-4">
              <p className="mb-2 text-white/30" style={{ fontSize: 11 }}>
                Launched by
              </p>
              <div className="flex items-center gap-3">
                <div
                  className="flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full font-bold text-[#c4f70e]"
                  style={{
                    width: 32,
                    height: 32,
                    background: 'rgba(196,247,14,0.1)',
                    fontSize: 14,
                  }}
                >
                  {(presale.launcher?.displayName ?? 'A').charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-medium text-white" style={{ fontSize: 14 }}>
                    {presale.launcher?.displayName ?? 'Anonymous'}
                  </p>
                  {presale.launcher?.presaleCount !== undefined && (
                    <p className="text-white/30" style={{ fontSize: 11 }}>
                      {presale.launcher.presaleCount} launches
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Block-0 badge */}
            <div
              className="flex items-center gap-3 rounded-xl p-4"
              style={{
                background: 'rgba(196,247,14,0.04)',
                border: '1px solid rgba(196,247,14,0.1)',
              }}
            >
              <div
                className="flex flex-shrink-0 items-center justify-center rounded-lg"
                style={{
                  width: 32,
                  height: 32,
                  background: 'rgba(196,247,14,0.12)',
                  color: '#c4f70e',
                }}
              >
                <IconLightning />
              </div>
              <div>
                <p className="font-semibold text-[#c4f70e]" style={{ fontSize: 13 }}>
                  Block-0 Entry
                </p>
                <p className="text-white/40" style={{ fontSize: 11 }}>
                  Get in before anyone else
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Shimmer keyframes */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
      `}</style>
    </div>
  );
}

// ── Post-Launch View ───────────────────────────────────

function PostLaunchView({ presale }: { presale: Presale }): ReactElement {
  const [vault, setVault] = useState<VaultStatus | null>(null);
  const [epochs, setEpochs] = useState<RewardEpoch[]>([]);
  const contributions = presale.contributions ?? [];

  useEffect(() => {
    vaultApi
      .getStatus(presale.id)
      .then(setVault)
      .catch(() => {});
    vaultApi
      .getEpochs(presale.id)
      .then(setEpochs)
      .catch(() => {});
  }, [presale.id]);

  const cardStyle =
    'bg-white/[0.015] border border-white/[0.06] rounded-[14px] p-9 transition-all duration-300 hover:border-[#c4f70e]/[0.15] hover:shadow-[0_0_40px_rgba(196,247,14,0.08)]';

  return (
    <div className="space-y-6">
      {/* Token Header */}
      <div className="flex items-center gap-5">
        {presale.imageUrl ? (
          <img src={presale.imageUrl} alt={presale.tokenName} className="h-14 w-14 rounded-full" />
        ) : (
          <div
            className="flex items-center justify-center rounded-full text-xl font-bold text-[#c4f70e]"
            style={{ width: 56, height: 56, background: 'rgba(196,247,14,0.1)' }}
          >
            {presale.tokenSymbol.charAt(0)}
          </div>
        )}
        <div>
          <h1 className="text-[28px] font-bold text-white">
            {presale.tokenName}{' '}
            <span className="font-mono text-xl text-white/30">${presale.tokenSymbol}</span>
          </h1>
          {presale.tokenMint && (
            <a
              href={`https://pump.fun/coin/${presale.tokenMint}`}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-[#c4f70e] hover:underline"
              style={{ fontSize: 12 }}
            >
              {presale.tokenMint.slice(0, 8)}…{presale.tokenMint.slice(-4)}
            </a>
          )}
        </div>
        <span
          className="ml-auto rounded-full px-3 py-1 text-[11px] font-semibold"
          style={{
            background: 'rgba(74,222,128,0.1)',
            color: '#4ade80',
            border: '1px solid rgba(74,222,128,0.2)',
          }}
        >
          LIVE
        </span>
      </div>

      {/* DexScreener Chart */}
      {presale.tokenMint && (
        <div
          className="overflow-hidden rounded-[14px] border border-white/[0.06]"
          style={{ height: 400 }}
        >
          <iframe
            src={`https://dexscreener.com/solana/${presale.tokenMint}?embed=1&theme=dark&info=0`}
            className="h-full w-full border-0"
            title="DexScreener Chart"
          />
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {[
          { label: 'Total Raised', value: `${Number(presale.totalRaised).toFixed(2)} SOL` },
          { label: 'Buyers', value: presale.buyerCount.toString() },
          {
            label: 'Vault Balance',
            value: vault ? `${Number(vault.currentBalanceSol).toFixed(4)} SOL` : '—',
          },
          {
            label: 'Distributed',
            value: vault ? `${Number(vault.totalDistributed).toFixed(4)} SOL` : '—',
          },
        ].map(({ label, value }) => (
          <div key={label} className={cardStyle}>
            <p className="mb-1 text-white/30" style={{ fontSize: 12 }}>
              {label}
            </p>
            <p className="font-mono font-bold text-white" style={{ fontSize: 18 }}>
              {value}
            </p>
          </div>
        ))}
      </div>

      {/* Epoch History */}
      <div className={cardStyle}>
        <SectionLabel icon={<IconVault />} text="Reward Epochs" />
        {epochs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full" style={{ fontSize: 13 }}>
              <thead>
                <tr className="border-b border-white/[0.06]">
                  {['Epoch', 'Period', 'Distributed', 'Status'].map((h) => (
                    <th
                      key={h}
                      className="py-3 text-left font-normal text-white/30"
                      style={{ fontSize: 11, letterSpacing: '1px' }}
                    >
                      {h.toUpperCase()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {epochs.map((epoch: RewardEpoch) => (
                  <tr key={epoch.id} className="border-b border-white/[0.04] hover:bg-white/[0.01]">
                    <td className="py-3 font-mono text-white">#{epoch.epochNumber}</td>
                    <td className="py-3 text-white/50" style={{ fontSize: 12 }}>
                      {new Date(epoch.periodStart).toLocaleDateString()} –{' '}
                      {new Date(epoch.periodEnd).toLocaleDateString()}
                    </td>
                    <td className="py-3 font-mono text-white">
                      {Number(epoch.amountDistributed).toFixed(4)} SOL
                    </td>
                    <td className="py-3">
                      <span
                        className="rounded-full px-2.5 py-0.5"
                        style={{
                          fontSize: 11,
                          background:
                            epoch.status === 'PAID'
                              ? 'rgba(74,222,128,0.1)'
                              : epoch.status === 'ACCUMULATING'
                                ? 'rgba(234,179,8,0.1)'
                                : 'rgba(255,255,255,0.06)',
                          color:
                            epoch.status === 'PAID'
                              ? '#4ade80'
                              : epoch.status === 'ACCUMULATING'
                                ? '#eab308'
                                : 'rgba(255,255,255,0.4)',
                        }}
                      >
                        {epoch.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-white/30" style={{ fontSize: 13 }}>
            No epochs yet — rewards accumulate after the first epoch period.
          </p>
        )}
      </div>

      {/* Holder Distribution */}
      <div className={cardStyle}>
        <SectionLabel icon={<IconPie />} text="Presale Buyers" />
        {contributions.length > 0 ? (
          <div className="space-y-3">
            {contributions.map((c) => (
              <div key={c.id} className="flex items-center gap-4 py-2">
                <span className="flex-shrink-0 font-mono text-white/50" style={{ fontSize: 12 }}>
                  {c.buyerPublicKey.slice(0, 6)}…{c.buyerPublicKey.slice(-4)}
                </span>
                <div
                  className="flex-1 overflow-hidden rounded-full"
                  style={{ height: 8, background: 'rgba(255,255,255,0.04)' }}
                >
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Number(c.percentage)}%`,
                      background: 'rgba(196,247,14,0.6)',
                    }}
                  />
                </div>
                <span className="flex-shrink-0 font-mono text-white" style={{ fontSize: 13 }}>
                  {Number(c.percentage).toFixed(1)}%
                </span>
                <span className="flex-shrink-0 text-white/30" style={{ fontSize: 12 }}>
                  +{Number(c.totalRewardsSol).toFixed(4)} SOL
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-white/30" style={{ fontSize: 13 }}>
            No contributor data available.
          </p>
        )}
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────

export function PresaleDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const [presale, setPresale] = useState<Presale | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPresale = (): void => {
    if (!id) return;
    presaleApi
      .get(id)
      .then(setPresale)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Failed to load presale')
      )
      .finally(() => setIsLoading(false));
  };

  useEffect(loadPresale, [id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div
          className="animate-spin rounded-full"
          style={{
            width: 36,
            height: 36,
            border: '2px solid rgba(196,247,14,0.15)',
            borderTopColor: '#c4f70e',
          }}
        />
      </div>
    );
  }

  if (error ?? !presale) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <p className="mb-4 text-red-400" style={{ fontSize: 14 }}>
          {error ?? 'Presale not found'}
        </p>
        <Link to="/presale" className="text-[#c4f70e] hover:underline" style={{ fontSize: 13 }}>
          Back to presales
        </Link>
      </div>
    );
  }

  const isLive = presale.status === 'LIVE' || presale.status === 'COMPLETED';

  return (
    <div style={{ background: '#08080a', minHeight: '100%' }}>
      {/* Nav bar */}
      <div className="mb-7 flex items-center justify-between gap-4">
        <Link
          to="/presale"
          className="inline-flex items-center gap-2 text-white/30 transition-colors hover:text-[#c4f70e]"
          style={{ fontSize: 13 }}
        >
          <IconArrowLeft />
          Back to presales
        </Link>
        <Link
          to={`/presale/${presale.id}/manage`}
          className="inline-flex items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-2 text-white/60 transition-all hover:border-[#c4f70e]/30 hover:text-[#c4f70e]"
          style={{ fontSize: 13 }}
        >
          <IconSettings />
          Manage
        </Link>
      </div>

      {isLive ? (
        <PostLaunchView presale={presale} />
      ) : (
        <PreLaunchView presale={presale} onRefresh={loadPresale} />
      )}
    </div>
  );
}
